import { ApolloClient, InMemoryCache } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { ClerkProvider, useAuth, useOrganization, useUser } from '@clerk/clerk-react';
import { createApolloClient, reactiveVars } from '../client/createApolloClient';
import { useState, useEffect, useCallback, useRef } from 'react';

export interface ApolloAuthClientProps {
  children: React.ReactNode;
  context: Record<string, any>;
  clerkKey?: string;
  apiUrl: string;
  cache?: InMemoryCache;
}

export interface OrganizationProps {
  name: string;
  id: string;
}

export interface UserDetailsProps {
  userId: string;
  firstName?: string;
  lastName?: string;
  email: string;
  orgId: string;
}

export const CustomProvider: React.FC<Omit<ApolloAuthClientProps, 'clerkKey'>> = ({
  children,
  context,
  apiUrl,
  cache,
}) => {
  const [client, setClient] = useState<ApolloClient | null>(null);
  const { getToken, isLoaded } = useAuth();
  const { organization } = useOrganization();
  const { user } = useUser();
  const prevAuthKeyRef = useRef<string>('');
  const prevApiUrlRef = useRef<string>('');
  const prevContextRef = useRef<Record<string, any> | null>(null);
  const prevCacheRef = useRef<typeof cache>(undefined);

  const setupClient = useCallback(async () => {
    if (isLoaded) {
      let token: string | null = null;
      try {
        token = await getToken({ template: 'default', skipCache: false });
      } catch (err) {
        console.error('Error setting up Apollo client:', err);
        token = null;
      }

      const updatedContext = {
        ...context,
        apiUrl,
        token,
        organization: organization
          ? {
              id: organization.id,
              name: organization.name,
            }
          : null,
        userDetails: user
          ? {
              userId: user?.id,
              firstName: user?.firstName,
              lastName: user?.lastName,
              email: user?.emailAddresses[0].emailAddress,
              orgId: organization?.id,
            }
          : null,
      };

      const newClient = createApolloClient(
        () => getToken({ template: 'default', skipCache: false }),
        updatedContext,
        apiUrl,
        cache
      );
      setClient(newClient);
    }
  }, [isLoaded, getToken, apiUrl, context, cache]);

  useEffect(() => {
    const authKey = `${user?.id ?? 'no-user'}|${organization?.id ?? 'no-org'}`;
    const shouldRecreateClient =
      !client ||
      authKey !== prevAuthKeyRef.current ||
      apiUrl !== prevApiUrlRef.current ||
      context !== prevContextRef.current ||
      cache !== prevCacheRef.current;

    if (shouldRecreateClient) {
      setupClient();
      prevAuthKeyRef.current = authKey;
      prevApiUrlRef.current = apiUrl;
      prevContextRef.current = context;
      prevCacheRef.current = cache;
    }
  }, [organization?.id, user?.id, apiUrl, context, cache, setupClient, client]);

  useEffect(() => {
    const updateUserDetails = async () => {
      if (organization && client) {
        reactiveVars.organization({
          id: organization?.id,
          name: organization?.name,
        });
      }
    };
    updateUserDetails();
  }, [organization, client]);

  useEffect(() => {
    if (user && client) {
      reactiveVars.userDetails({
        userId: user?.id,
        firstName: user?.firstName,
        lastName: user?.lastName,
        email: user?.emailAddresses[0].emailAddress,
        orgId: organization?.id,
      } as UserDetailsProps);
    }
  }, [user, client, organization]);

  if (!client) {
    return null;
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export const ApolloAuthClient: React.FC<ApolloAuthClientProps> = ({
  children,
  context,
  clerkKey,
  apiUrl,
  cache,
}) => {
  if (!clerkKey) {
    return <div>Clerk key is missing</div>;
  }

  return (
    <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
      <CustomProvider context={context} apiUrl={apiUrl} cache={cache}>
        {children}
      </CustomProvider>
    </ClerkProvider>
  );
};

export default ApolloAuthClient;
